/** biome-ignore-all lint/suspicious/noArrayIndexKey: fix index */
import cx from 'classnames';
import {useCallback, useContext} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {canvasRemoveTextBordersAndIcons} from '~/common/canvas/customs/text_border';
import {useNavigator} from '~/common/FabricInvitation/EventCreateDesign/components/CardDesignNavigator/useNavigator';
import {Sections} from '~/common/FabricInvitation/EventCreateDesign/constants';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {
  selectCurrentSection,
  selectIgnorePremiumWarning,
  selectLoaded,
  selectPremiumFeaturesPristine,
} from '~/common/fabric/reducers/fabricDesign/selectors';
import {Button as PBButton} from '~library/atoms/Button';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {DiamondFillIcon} from '~library/svg/DiamondFillIcon';
import {largishAndUp} from '~libSass/base/_exports.sass';

import './Button.sass';

export const Button = ({
  title,
  icon,
  section,
  editor = '',
  disableAutoOpenEditor,
  highLight = false,
  premiumFeature = false,
  isPremium = false,
  onClickCallback,
}) => {
  const {canvas} = useContext(EventCreateContext);
  const currentSection = useSelector(selectCurrentSection);
  const loaded = useSelector(selectLoaded);
  const {matches: isDesktop} = useMatchQuery(`(min-width: ${largishAndUp})`);
  const isMobile = !isDesktop;
  const navigate = useNavigator({isMobile, disableAutoOpenEditor});
  const dispatch = useDispatch();
  const ignorePremiumWarning = useSelector(selectIgnorePremiumWarning);
  const premiumFeaturesPristine = useSelector(selectPremiumFeaturesPristine);

  const onClickHandler = useCallback(() => {
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {
        activeCardEditor: editor,
        touched: true,
      },
    });

    // Remove text borders when navigating away from card canvas editor
    if ([Sections.STAMP, Sections.BACKGROUND].includes(section)) {
      canvasRemoveTextBordersAndIcons(canvas);
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          shouldTogglePremiumBackground: true,
        },
      });
    } else if (section === Sections.CARD_DESIGN) {
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          editorDisabled: currentSection !== section,
          shouldTogglePremiumBackground: false,
        },
      });
    } else if (section === Sections.ENVELOPE_LINER) {
      canvasRemoveTextBordersAndIcons(canvas);
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          shouldTogglePremiumBackground: true,
          pristineEnvelopeDesign: false,
        },
      });
    }

    if (section !== Sections.CARD_DESIGN && section !== Sections.UPLOAD_PHOTO) {
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          usedPremiumFeature: true,
        },
      });
      if (!ignorePremiumWarning) {
        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {
            premiumFeaturesWarning: false,
            ignorePremiumWarning: true,
          },
        });
      }
    } else if (premiumFeaturesPristine) {
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          usedPremiumFeature: false,
        },
      });
    }
    navigate(section);

    if (onClickCallback) {
      onClickCallback();
    }
  }, [
    editor,
    canvas,
    premiumFeaturesPristine,
    section,
    dispatch,
    currentSection,
    onClickCallback,
    ignorePremiumWarning,
    navigate,
  ]);

  const buttonTitle = typeof title === 'string' ? [title] : title;

  const activeClass = () => {
    return currentSection === section
      ? 'im_create_flow__nav_bottom__section--active'
      : 'im_create_flow__nav_buttom__section';
  };

  const activeButtonClass = () => {
    return currentSection === section ? 'active' : '';
  };

  return (
    // TODO: These are for mobile testing
    // biome-ignore lint/a11y/noStaticElementInteractions: add accessibility props
    <div
      className={cx('im_create_flow__nav_bottom__section', {[activeClass()]: !!activeClass()})}
      role="presentation"
      data-qa-id={`nav-bottom-${section}`}
      title={buttonTitle.join(' ')}
      onClick={onClickHandler}
    >
      <div className="im_create_flow__nav_bottom__section__button-wrapper">
        <PBButton
          id="navBottom"
          className={cx('im_create_flow__nav_bottom__section__button', {
            'im_create_flow__nav_bottom__section__button--premium-preview':
              !isPremium && premiumFeature,
            'im_create_flow__nav_bottom__section__button--highlighted': highLight,
            [activeButtonClass()]: !!activeButtonClass(),
          })}
          variant="transparent"
          size="medium"
          disabled={!loaded}
          data-qa-id={`nav-bottom-${section}-button`}
        >
          {icon}
        </PBButton>
        {!isPremium && premiumFeature && (
          <DiamondFillIcon className="premium-diamond" ratio={isDesktop ? 0.3 : 0.25} />
        )}
      </div>
      <div className="im_create_flow__nav_bottom__section__title__wrapper">
        {buttonTitle.map((rowTitle, index) => (
          <p key={`${rowTitle}-${index}`} className="im_create_flow__nav_bottom__section__title">
            {rowTitle}
          </p>
        ))}
      </div>
    </div>
  );
};
